import React, { useEffect, useMemo, useRef, useState } from "react";
import { default as content } from '../content/en';

export default function PostGridAddressAutocomplete({
  apiKey,
  mode = "intl",                 // "intl" or "standard"
  countryFilter = ["GB"],         // narrow results; change as needed
  placeholder = content.postGrid.searchPlaceholder,
  onSelect,
  initialValue
}) {
  const [query, setQuery] = useState(initialValue || "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const abortRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const [lastCompleted, setLastCompleted] = useState(null); // store last completion payload
  const [formattedCache, setFormattedCache] = useState(""); // cached one-liner

  function toLine1(addr) {
  const a = addr?.address || addr;
    return a?.line1 || "";
  }

  function toFormatted(addr) {
    const a = addr?.address || addr;
    const oneLine =
        a?.formatted ||
        [a?.line1, a?.line2, a?.city, a?.prov || a?.state, a?.pc || a?.postalOrZip, a?.country]
        .filter(Boolean)
        .join(", ");
    return oneLine;
  }

  // Treat only true addresses as completable (intl mode)
  function isAddressItem(s) {
    const t = (s?.type || s?.preview?.type || "").toLowerCase();
    return t === "address";
  }

  const base = mode === "intl" ? "intl_addver" : "addver";

  const previewURL = useMemo(() => {
    const u = new URL(`https://api.postgrid.com/v1/${base}/completions`);
    if (query.trim()) u.searchParams.set("partialStreet", query.trim());
    countryFilter?.forEach(c => u.searchParams.append("countryFilter", c));
    return u.toString();
  }, [base, query, countryFilter]);

  // Debounced GET previews (no lookup)
  useEffect(() => {
    if (!apiKey) return;
    if (abortRef.current) abortRef.current.abort();

    const q = query.trim();
    if (q.length == 0 && lastCompleted) {
      setLastCompleted(null);
    }
    if (q.length < 3) {
      setSuggestions([]);
      setOpen(false);
      setLoading(false);
      return;
    }

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);

    if (lastCompleted) {
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(previewURL, {
          method: "GET",
          headers: { "x-api-key": apiKey },
          signal: ctrl.signal,
        });
        if (!res.ok) throw new Error(`Preview HTTP ${res.status}`);
        const json = await res.json();
        const items = Array.isArray(json?.data) ? json.data : [];
        setSuggestions(items);
        setOpen(true);
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("[PostGrid] preview error:", e);
          setSuggestions([]);
          setOpen(false);
        }
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => { clearTimeout(t); ctrl.abort(); };
  }, [previewURL, apiKey, query]);

  // POST completion (uses 1 lookup)
  async function completeSelection(item) {
    const isCompletable = !!item?.id || item?.type === "Address";
    if (!isCompletable) return; // ignore BuildingNumber etc.

    try {
      setLoading(true);
      const url = `https://api.postgrid.com/v1/${base}/completions`;
      const body = item?.id ? { id: item.id } : (item?.index != null ? { index: item.index } : null);
      if (!body) return;

      const res = await fetch(url, {
        method: "POST",
        headers: { "x-api-key": apiKey, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Completion HTTP ${res.status}`);
      const data = await res.json();
      const full = data?.data || data;

      // Display a friendly string after selection
      // Build a one-line string from all available parts
      const addr = full?.address || full; // some results nest under address
      const formatted = toFormatted(full);
      setQuery(formatted);           // show full one-line after selection
      setLastCompleted(full);        // remember the completed address
      setFormattedCache(formatted);  // cache the one-liner
      setOpen(false);
      setSuggestions([]);
      setActiveIdx(-1);
      onSelect?.(full);
    } catch (e) {
      console.error("[PostGrid] completion error:", e);
    } finally {
      setLoading(false);
    }
  }

  function onKeyDown(e) {
    if (!open || !suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (activeIdx >= 0) {
        e.preventDefault();
        completeSelection(suggestions[activeIdx]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    function onDocClick(e) {
      if (!listRef.current || !inputRef.current) return;
      if (!listRef.current.contains(e.target) && !inputRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Helper: build a human-friendly line for each preview
  function displayForPreview(s) {
    // console.log("suggestion s is:", s);
    const p = s?.preview || s;
    // Prefer any formatted/text fields if present
    const formatted = p?.formatted || p?.description || p?.text || p?.display || p?.displayText;
    if (formatted) return formatted;
    /*addr?.line1,
        addr?.line2,
        addr?.city,
        addr?.prov || addr?.state,
        addr?.country,
        addr?.pc || addr?.postal_code || addr.postalOrZip,*/
    const addr =
      p?.address ||
      p?.line1 ||
      p?.address_line1 ||
      p?.street ||
      "";
    //   console.log("Addr part:", addr);
    const locality =
      p?.city || p?.locality || p?.municipality || "";
    const region =
      p?.prov || p?.state || p?.region || "";
    const pc = p?.pc || p?.postal_code || p?.zip || "";
    return [addr, locality, region, pc].filter(Boolean).join(", ");
  }

  return (
    <div style={{ position: "relative", maxWidth: 520 }}>
        {/* Anchor container for input + dropdown */}
        <div style={{ position: "relative" }}>
            <input
            id="pg-line1"
            ref={inputRef}
            type="text"
            value={query}
            placeholder={placeholder}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onKeyDown={onKeyDown}
            onFocus={() => {
                if (lastCompleted) {
                 // collapse to line1 when user starts editing
                 setQuery(toLine1(lastCompleted));
               }
             }}
             onBlur={() => {
               if (!lastCompleted) return;
               const currentLine1 = toLine1(lastCompleted);
               // If user didn't meaningfully change it, re-expand to full formatted
               const unchanged = query.trim() === "" || query.trim() === currentLine1.trim();
               if (unchanged) setQuery(formattedCache || toFormatted(lastCompleted));
             }}
            autoComplete="off"
            style={{ width: "100%", padding: "10px", border: "1px solid #ccc", borderRadius: 8, margin: 0 }}
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls="pg-suggestions"
            role="combobox"
            />

            {/* ↓ keep the UL inside the anchor container */}
            {open && (loading || suggestions.length > 0) && (
            <ul
                id="pg-suggestions"
                ref={listRef}
                role="listbox"
                style={{
                position: "absolute",
                top: "calc(100% + 6px)",
                left: 0,
                right: 0,
                margin: 0,
                padding: 0,
                listStyle: "none",
                background: "#fff",
                border: "1px solid #ccc",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                maxHeight: 280,
                overflowY: "scroll",
                zIndex: 1000,
                fontSize: 14,
                }}
            >
                {loading && (
                <li style={{ padding: "10px 14px", fontStyle: "italic", color: "#666" }}>
                    {content.postGrid.searchingMessage}
                </li>
                )}

                {/* Separate previews into address vs. bucket types */}
                {(() => {
                const addressItems = suggestions.filter(isAddressItem);
                const bucketItems  = suggestions.filter(s => !isAddressItem(s));

                return (
                    <>
                    {/* --- Address suggestions --- */}
                    {addressItems.map((s, i) => {
                        const label = displayForPreview(s) || "(address)";
                        const isActive = i === activeIdx;
                        return (
                        <li
                            key={(s.id ?? s.index ?? i) + "-" + i}
                            role="option"
                            aria-selected={isActive}
                            onMouseEnter={() => setActiveIdx(i)}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => completeSelection(s)}
                            style={{
                            padding: "10px 14px",
                            cursor: "pointer",
                            background: isActive ? "#ff6a00" : "#fff",
                            color: isActive? "#fff" : "#111",
                            display: "flex",
                            flexDirection: "column",
                            transition: "background 0.1s ease-in-out",
                            }}
                        >
                            <span style={{ fontWeight: 500 }}>{label}</span>
                        </li>
                        );
                    })}

                    {/* --- Street/locality buckets --- */}
                    {bucketItems.length > 0 && (
                        <>
                        <li
                            style={{
                            padding: "6px 14px",
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#666",
                            borderTop: "1px solid #eee",
                            }}
                        >
                            {content.postGrid.moreLocationsMessage}
                        </li>
                        {bucketItems.map((s, i) => {
                            const label =
                            displayForPreview(s)
                                .replace(/^\s*\d+\s+addresses\s*$/i, "")
                                .trim() || "Other area";
                            return (
                            <li
                                key={"bucket-" + i}
                                style={{
                                padding: "10px 14px",
                                color: "#888",
                                background: "#fafafa",
                                cursor: "not-allowed",
                                borderTop: "1px solid #f3f3f3",
                                }}
                                title={content.postGrid.refineTitle}
                            >
                                {label && <span>{label}</span>}
                                <span style={{ fontSize: 12, marginLeft: 6 }}>
                                ({s?.preview?.count || s?.count || content.postGrid.several } {content.postGrid.addresses})
                                </span>
                            </li>
                            );
                        })}
                        </>
                    )}
                    </>
                );
                })()}

                {!loading && suggestions.length === 0 && (
                <li style={{ padding: "10px 14px", fontStyle: "italic", color: "#777" }}>
                    {content.postGrid.noResults}
                </li>
                )}
            </ul>
            )}
        </div>

        {/* Tip lives OUTSIDE the anchor container so the dropdown can cover it */}
        <p style={{ fontSize: 14, color: "#949494", marginTop: 8 }}>
            {content.postGrid.tipMessage}
        </p>
        </div>

  );
}
